/**
 * `SidebarDrawer` — mobile/tablet sidebar overlay (Task 20).
 *
 * On desktop the file-tree sidebar is part of the persistent grid layout.
 * On tablet (<1024px) it collapses to an icon strip; on mobile (<768px)
 * it disappears entirely until the user taps the hamburger / "Files"
 * activity icon, at which point it slides in as a *drawer* — a modal
 * overlay above the content with a translucent backdrop.
 *
 * This component renders the drawer + backdrop. It is layout-agnostic:
 *   - The Shell decides *whether* to render a drawer (only when
 *     layout === 'mobile' and the sidebar is open).
 *   - The Shell passes the sidebar content as children.
 *   - This component owns the close affordances: tapping the backdrop,
 *     tapping the close button, or pressing Escape.
 *
 * Accessibility:
 *   - `role="dialog"` + `aria-modal="true"` — screen readers treat it
 *     as a modal so focus is implied to be inside it.
 *   - `aria-label` is required by props (the caller knows what's inside).
 *   - Escape key closes — wired via `keydown` listener attached to
 *     `document` while the drawer is open, then removed on close. We do
 *     not trap focus inside the drawer — keyboard users on mobile are
 *     rare and a full focus trap requires substantial DOM probing that
 *     the file-tree already handles via its own `tabIndex` props.
 *
 * Touch:
 *   - The drawer is full-height and ~80vw wide — tap targets in the
 *     file tree are already ≥32px (set in obsidian-vars.css padding +
 *     line-height), satisfying the WCAG 2.5.5 enhanced touch target
 *     recommendation when the user is on a phone-sized viewport.
 */

import { useEffect } from 'react';

export interface SidebarDrawerProps {
  /** Whether the drawer is currently visible. When false this component
   *  renders nothing (an empty fragment) — the parent does not need to
   *  conditionally render it. */
  readonly open: boolean;
  /** Called when the user taps the backdrop, the close button, or presses
   *  Escape. The parent owns the open/closed state. */
  readonly onClose: () => void;
  /** Required accessible label, e.g. "Vault file tree". */
  readonly ariaLabel: string;
  /** Drawer content (typically the same FileTree the desktop sidebar
   *  shows). */
  readonly children: JSX.Element | readonly JSX.Element[];
}

export function SidebarDrawer(props: SidebarDrawerProps): JSX.Element | null {
  const { open, onClose, ariaLabel, children } = props;

  // Escape closes — only while open. We attach to `document` not `window`
  // so a portal-rendered child (e.g. ConflictDialog) doesn't intercept.
  useEffect(() => {
    if (!open) return;
    if (typeof document === 'undefined') return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return (): void => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="ow-drawer-root" data-testid="sidebar-drawer-root">
      <button
        type="button"
        className="ow-drawer-backdrop"
        aria-label="Close sidebar"
        onClick={onClose}
        data-testid="sidebar-drawer-backdrop"
      />
      <aside
        className="ow-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        data-testid="sidebar-drawer"
      >
        <div className="ow-drawer-header">
          <span className="ow-drawer-title">{ariaLabel}</span>
          <button
            type="button"
            className="ow-drawer-close"
            aria-label="Close"
            title="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="ow-drawer-body">{children as JSX.Element}</div>
      </aside>
    </div>
  );
}

export default SidebarDrawer;
